import { useRef } from 'react'
import { useOnClickOutside } from '@nfinite/react-hubstairs/hooks'

import { TooltipContainer } from './styled'

/**
 * This is a wrapper of react-tooltip-lite,
 * Documentation at: https://www.npmjs.com/package/react-tooltip-lite
 *
 * @param children : target of the tooltip
 * @param content : content of the tooltip
 * @param className : class of Tootltip wrapper
 *
 * @param direction : tip direction, defaults to up. Possible values are "up", "down", "left", "right"
 * with optional modifer for alignment of "start" and "end".
 * e.g. "left-start" will attempt tooltip on left and align it with the start of the target.
 * If alignment modifier is not specified the default behavior is to align "middle".
 *
 * @param onToggle : this is called when the visibility of the tooltip changes.
 *
 * @param eventOn : full name of supported react event to show the tooltip, e.g.: 'onClick'
 * @param eventOff : full name of supported react event to hide the tooltip, e.g.: 'onClick'
 * @param useHover : whether to use hover to show/hide the tip, defaults to true
 *
 * @param distance : The distance from the tooltip to the target
 * @param zIndex : tooltip zIndex
 *
 * @param isOpen : forces open/close state from a prop, overrides hover or click state
 *
 * @param onClickOutside : callback on click outside
 *
 * @param tipContentHover	: defines whether you should be able to hover over the tip contents for links and copying content, defaults to true.
 */
function Tooltip({
  children,
  arrow = true,
  content,
  direction,
  onToggle,
  eventOn,
  eventOff,
  useHover,
  isOpen,
  distance = 10,
  className,
  onClickOutside,
  zIndex = 9992,
  forceDirection = false,
  arrowSize = 5,
  tipContentHover = true,
  dataTestId,
  hoverDelay,
  mouseOutDelay,
}) {
  return (
    <TooltipContainer
      data-testid={dataTestId}
      className={className}
      content={<Content handleOnClickOutside={isOpen ? onClickOutside : undefined}>{content}</Content>}
      direction={direction}
      forceDirection={forceDirection}
      onToggle={onToggle}
      eventOn={eventOn}
      eventOff={eventOff}
      useHover={useHover}
      isOpen={isOpen}
      padding={0}
      distance={distance}
      arrow={arrow}
      arrowSize={arrowSize}
      zIndex={zIndex}
      tipContentHover={tipContentHover}
      hoverDelay={hoverDelay}
      mouseOutDelay={mouseOutDelay}
    >
      {children}
    </TooltipContainer>
  )
}

function Content({ handleOnClickOutside = () => {}, children }) {
  const ref = useRef()
  useOnClickOutside(ref, handleOnClickOutside)

  return <div ref={ref}>{children}</div>
}

export default Tooltip
